import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Text } from 'grommet';
import { Document } from 'grommet-icons';

interface FileInputProps {
  onFileSelect: (file: File) => void;
  isUploading?: boolean;
  accept?: string;
  disabled?: boolean;
  selectedFile?: File | null;
  id?: string;
}

const FileInput: React.FC<FileInputProps> = ({
  onFileSelect,
  isUploading = false,
  accept = '',
  disabled = false,
  selectedFile = null,
  id = 'file-input',
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isHover, setIsHover] = useState(false);
  const isDisabled = !isUploading && !disabled;
  const { t } = useTranslation();

  const handleFileSelect = (file?: File) => {
    if (file) onFileSelect(file);
  };

  const handleDragEvents = (e: React.DragEvent, isOver: boolean) => {
    e.preventDefault();
    if (isDisabled) setIsDragOver(isOver);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (isDisabled && e.dataTransfer.files?.[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  return (
    <Box
      border={{ color: 'light-5', size: '1px' }}
      round="xsmall"
      pad={{ vertical: 'medium', horizontal: 'large' }}
      direction="row"
      align="center"
      justify="between"
      style={{
        opacity: isDisabled ? 1 : 0.5,
        borderColor: isDragOver || isHover ? '#00739D' : undefined,
        background: isDragOver || isHover ? '#F0F8FF' : undefined,
      }}
      onClick={() => isDisabled && document.getElementById(`${id}-input`)?.click()}
      onDragOver={(e) => handleDragEvents(e, true)}
      onDragLeave={(e) => handleDragEvents(e, false)}
      onDrop={handleDrop}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      <Box direction="row" align="center" gap="small" flex style={{ minWidth: 0 }}>
        <Box direction="row" wrap style={{ minWidth: 0 }}>
          {selectedFile ? (
            <>
              <Text color="accent-1" margin={{ right: 'xsmall' }} style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                {t('documents:selectedFile')}
              </Text>
              <Text color="accent-1" weight="bold" wordBreak="break-word">
                {selectedFile.name}
              </Text>
            </>
          ) : (
            <Text color="accent-1">
              {t('documents:dropFiles')}{' '}
              <Text color="accent-1" weight="bold">
                {t('documents:browse')}
              </Text>
            </Text>
          )}
        </Box>
        <Box margin={{ left: 'small' }} flex={false}>
          <Document />
        </Box>
      </Box>
      <input
        id={`${id}-input`}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={(e) => handleFileSelect(e.target.files?.[0])}
        disabled={!isDisabled}
      />
    </Box>
  );
};

export default FileInput;
